import { useRef, useState, Suspense, lazy } from "react";
import { Box } from "@mui/material";

import { error, success } from "constants";
import { postCall_v2, putCall } from "services";
import { useToaster } from "hooks";

import FileDragAndDrop from "../components/FileDragAndDrop";
import ReviewTransactions from "./ReviewTransactions";
import { CreditNotesSellerPayout } from "./constants";
import { constructInvalidTxList } from "../makeData";
import ManualInput from "./ManualInput";
import FileUploadWrapper from "../components/FileUploadWrapper";
import UpdateTransaction from "./UpdateTransaction";
import { generateOffErpRequestData } from "./makeData";
const PendingTransactions = lazy(() => import("./PendingTransactions"));

const {
  msgStatements: {
    ERROR_MSG,
    SAVED_SUCCESS_MSG,
    TRANSACTION_SAVED_MSG,
    TRANSACTION_UPDATED_MSG,
  },
} = CreditNotesSellerPayout;

const TxView = ({
  invalidCsvList,
  transaction,
  handleInvalidCsvList,
  handleClearAll,
  updateTransaction,
  handleSaveData,
  manualInputRef,
}) => {
  if (invalidCsvList.length && !transaction) {
    return (
      <ReviewTransactions
        invalidCsvList={invalidCsvList}
        passInvalidCsvList={handleInvalidCsvList}
        retryPayoutSubmission={handleSaveData}
        clearAll={handleClearAll}
      />
    );
  } else if (transaction) {
    return (
      <UpdateTransaction
        updateTransaction={(data) => updateTransaction(data)}
        transaction={transaction}
        key={transaction.id}
      />
    );
  } else {
    return (
      <ManualInput
        saveManualUpload={(data) => handleSaveData(data)}
        ref={manualInputRef}
      />
    );
  }
};

const CreditNotesSellerReturn = ({ filters, children }) => {
  const fileRef = useRef();
  const pendingTransactionsRef = useRef();
  const manualInputRef = useRef();
  const [invalidCsvList, setInvalidCsvList] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const showToaster = useToaster();

  /**
   * @param {*} csvData
   * @description - Triggered when a file is uploaded
   */

  const uploadOffERP_payouts = (csvData) => {
    const formattedData = generateOffErpRequestData(csvData);
    void handleSaveData(formattedData);
  };

  /**
   * @param {*} csvData
   * @description - Triggered when a file is uploaded
   */
  const handleSaveData = async (data) => {
    try {
      const formattedData = data.filter((ele) => !!ele);
      const res = await postCall_v2("/oms/off-oms-payout/creditAndSale", {
        offOmsCreditAndSalesChargeRtabDTOList: formattedData,
      });
      validateResponse(res?.data, formattedData);
    } catch {
      showToaster(ERROR_MSG, error);
    }
  };

  /**
   * @description Validate response and set invalidList
   */
  const validateResponse = (response, data, isUpdate = false) => {
    if (response && Object.keys(response).length) {
      const invalidPayloadList = Object.keys(response);
      handleTxResponse(invalidPayloadList, response, data);
    } else {
      const SUCCESS_MSG = isUpdate
        ? TRANSACTION_UPDATED_MSG
        : TRANSACTION_SAVED_MSG;
      fileRef.current.resetFile();
      showToaster(SUCCESS_MSG, success);
      setInvalidCsvList([]);
    }
    manualInputRef?.current?.reset();
    pendingTransactionsRef?.current.refetchPendingTransactions();
    setTransaction(null);
  };

  /**
   * @description Validate response and show toaster message
   */
  const handleTxResponse = (invalidPayloadList, res, data) => {
    if (invalidPayloadList.length > 0) {
      const errorStr = invalidPayloadList > 1 ? "errors" : "error";
      setInvalidCsvList(constructInvalidTxList(Object.values(res)));
      const errorMsg = `${
        data.length - invalidPayloadList.length
      } have been saved and ${invalidPayloadList.length} have ${errorStr}`;
      showToaster(errorMsg, error);
    } else {
      showToaster(SAVED_SUCCESS_MSG, success);
      fileRef.current.resetFile();
    }
  };

  /**
   * @param {*} csvList
   * @description set verified invalid list response as new list in review table
   */
  const handleInvalidCsvList = (csvList) => {
    setInvalidCsvList(csvList);
  };

  const updateTransaction = async (data) => {
    const isUpdateTransaction = true;
    try {
      const result = await putCall(
        `/oms/off-oms-payout/creditAndSale?payoutId=${data.id}`,
        { offOmsCreditAndSalesChargeRtabDTOList: [data] },
        {},
        () => showToaster(ERROR_MSG, error),
      );
      if (result) {
        validateResponse(result, data, isUpdateTransaction);
      }
    } catch {
      showToaster(ERROR_MSG, error);
    }
  };

  const handleClearAll = () => {
    fileRef.current.resetFile();
  };

  return (
    <>
      <FileUploadWrapper>
        <Box mb={6}>
          <FileDragAndDrop
            ref={fileRef}
            invokeOMSPayout={(csvData) => uploadOffERP_payouts(csvData)}
          />
        </Box>
        <TxView
          invalidCsvList={invalidCsvList}
          transaction={transaction}
          handleInvalidCsvList={handleInvalidCsvList}
          uploadOffERP_payouts={uploadOffERP_payouts}
          handleClearAll={handleClearAll}
          updateTransaction={updateTransaction}
          handleSaveData={handleSaveData}
          manualInputRef={manualInputRef}
        />
      </FileUploadWrapper>
      {children}
      <Suspense fallback={<Box> Loading... </Box>}>
        <PendingTransactions
          filters={filters}
          ref={pendingTransactionsRef}
          editTransaction={(transaction) => setTransaction(transaction)}
        />
      </Suspense>
    </>
  );
};

export default CreditNotesSellerReturn;
